import React from 'react';
import Select from '@/components/common/ReactSelect5';
import Pill from '@/components/ui/Pill';
import Text from '@/components/ui/Text';
import cn from '@/utils/cn';
import { SearchInput } from '../LiveSessionsMonitor/tableShared';
import { CATEGORIES } from './eventCatalog';
import { DEFAULT_FILTERS, type EventCategory, type EventSource, type Filters } from './types';

export interface ActorOption {
  label: string;
  value: string;
}

interface FilterBarProps {
  actors: ActorOption[];
  filters: Filters;
  shownCount: number;
  totalCount: number;
  onChange: (filters: Filters) => void;
}

const SOURCE_OPTIONS: { label: string; value: EventSource | 'all' }[] = [
  { label: 'All', value: 'all' },
  { label: 'Server', value: 'server' },
  { label: 'Client', value: 'client' },
];

const SEGMENT_CLASS = (active: boolean) => cn(
  'tw-px-3 tw-py-1 tw-text-xs tw-border tw-border-solid tw-border-tci-purple tw-cursor-pointer',
  'first:tw-rounded-l last:tw-rounded-r -tw-ml-px first:tw-ml-0',
  active ? 'tw-bg-tci-purple tw-text-white' : 'tw-bg-white tw-text-tci-purple hover:tw-bg-tci-purple/10',
);

const CLEAR_BUTTON_CLASS = cn(
  'tw-bg-transparent tw-border-0 tw-p-0 tw-text-xs tw-text-tci-purple tw-cursor-pointer hover:tw-underline',
);

const FilterBar = ({ actors, filters, shownCount, totalCount, onChange }: FilterBarProps) => {
  const toggleCategory = (category: EventCategory) => {
    const categories = filters.categories.includes(category)
      ? filters.categories.filter(c => c !== category)
      : [...filters.categories, category];
    onChange({ ...filters, categories });
  };

  const isDefault = filters.actor === 'all' && filters.categories.length === 0
    && filters.query === '' && filters.source === 'all';

  return (
    <div className="tw-flex tw-flex-col tw-gap-3">
      <div className="tw-flex tw-items-center tw-gap-3 tw-flex-wrap">
        <div className="tw-w-64">
          <Select
            aria-label="Filter by actor"
            options={actors}
            value={actors.find(option => option.value === filters.actor) ?? actors[0]}
            onChange={option => onChange({ ...filters, actor: (option as ActorOption | null)?.value ?? 'all' })}
          />
        </div>
        <div aria-label="Event source" className="tw-flex" role="group">
          {SOURCE_OPTIONS.map(option => (
            <button
              key={option.value}
              aria-pressed={filters.source === option.value}
              className={SEGMENT_CLASS(filters.source === option.value)}
              type="button"
              onClick={() => onChange({ ...filters, source: option.value })}
            >
              {option.label}
            </button>
          ))}
        </div>
        <SearchInput
          placeholder="Search event, reason, slide…"
          value={filters.query}
          onChange={query => onChange({ ...filters, query })}
        />
        <Text className="tw-text-very-dark-grey tw-ml-auto tw-m-0" size="xs" tag="span">
          {shownCount.toLocaleString()} of {totalCount.toLocaleString()} events
        </Text>
        {!isDefault && (
          <button className={CLEAR_BUTTON_CLASS} type="button" onClick={() => onChange(DEFAULT_FILTERS)}>
            Clear filters
          </button>
        )}
      </div>

      <div aria-label="Event categories" className="tw-flex tw-gap-2 tw-flex-wrap" role="group">
        {CATEGORIES.map((category) => {
          const active = filters.categories.includes(category.key);
          return (
            <button
              key={category.key}
              aria-pressed={active}
              className="tw-bg-transparent tw-border-0 tw-p-0 tw-cursor-pointer"
              type="button"
              onClick={() => toggleCategory(category.key)}
            >
              <Pill outline={!active} pillColor="purple" solid={active}>
                <Text size="xs" tag="span">{category.label}</Text>
              </Pill>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default FilterBar;
